import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ReactComponent as BlueCheck } from '../../../assets/images/svg/blue-check.svg';
import { ReactComponent as RedCross } from '../../../assets/images/svg/red-cross.svg';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const DOT = 24;

const CompletedIcon = styled(BlueCheck)`
  flex-shrink: 0;
  width: ${pxToRem(DOT)};
  height: ${pxToRem(DOT)};
  z-index: 1;
`;

const CancelledIcon = styled(RedCross)`
  flex-shrink: 0;
  width: ${pxToRem(DOT)};
  height: ${pxToRem(DOT)};
  z-index: 1;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  gap: ${pxToRem(12)};
  position: relative;
  padding-bottom: ${pxToRem(24)};

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: ${pxToRem(DOT / 2)};
    transform: translateX(-50%);
    top: ${pxToRem(DOT)};
    bottom: 0;
    width: 1px;
    background: ${themeColors.colorBorderPrimary};
  }
`;

const Dot = styled.div`
  flex-shrink: 0;
  width: ${pxToRem(DOT)};
  height: ${pxToRem(DOT)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  background: ${themeColors.colorWhite};
  border: 1px solid ${themeColors.colorBorderPrimary};
`;

const PendingDot = styled.span`
  width: ${pxToRem(6)};
  height: ${pxToRem(6)};
  border-radius: 50%;
  background: ${themeColors.colorTextSecondary};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(2)};
  min-width: 0;
`;

const Label = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? themeColors.colorPrimary : themeColors.colorTextSecondary};
`;

const SubText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextSecondary};
`;

const CancelLink = styled.button`
  align-self: flex-start;
  margin-top: ${pxToRem(4)};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorError};
  text-decoration: underline;
  text-decoration-skip-ink: auto;
`;

const SellFromHomeStatusTimeline = ({ steps, cancelLabel, onCancelOrder }) => (
  <List data-cy="sfh-status-timeline">
    {steps.map((step) => {
      const active = Boolean(step.completed || step.cancelled);
      return (
        <Item key={step.key}>
          {step.completed && !step.cancelled && <CompletedIcon />}
          {step.cancelled && <CancelledIcon />}
          {!step.completed && !step.cancelled && (
            <Dot>
              <PendingDot />
            </Dot>
          )}
          <Content>
            <Label $active={active}>{step.label}</Label>
            {step.subText && <SubText>{step.subText}</SubText>}
            {step.showCancel && typeof onCancelOrder === 'function' && (
              <CancelLink
                type="button"
                onClick={onCancelOrder}
                data-cy="link-sfh-cancel-order"
              >
                {cancelLabel}
              </CancelLink>
            )}
            {step.notice}
          </Content>
        </Item>
      );
    })}
  </List>
);

SellFromHomeStatusTimeline.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      completed: PropTypes.bool,
      cancelled: PropTypes.bool,
      subText: PropTypes.string,
      showCancel: PropTypes.bool,
      notice: PropTypes.node,
    }),
  ),
  cancelLabel: PropTypes.string,
  onCancelOrder: PropTypes.func,
};

export default SellFromHomeStatusTimeline;
